import {
  STORMWATER_OUTLET_LABELS,
  STORMWATER_OUTLETS,
  effectFor,
  outdoorAreaTotals,
  outdoorElementMatrix,
  setOutdoorElement,
  type ClientAnomalyEffect,
  type OutdoorElement,
  type OutdoorElementCell,
  type SurveyV2,
  type VocabularyEntry,
} from '@ghp/domain'
import {
  Card,
  CardBody,
  CountInput,
  InfoNote,
  Input,
  SectionLabel,
  ToggleChip,
  ToggleChipGroup,
} from '@ghp/ui'
import { Info, TriangleAlert, Waves } from 'lucide-react'
import { useId, useState } from 'react'

import { CheckRow } from './CheckRow'
import s from './StormwaterCard.module.css'
import grid from './SurveyForm.module.css'

/**
 * Carte « Eaux pluviales » de l'onglet « Relevé » (#412) : le **tableau de la fiche papier**, un
 * élément par ligne, un exutoire par colonne — et la même grille que les points d'eau, une colonne
 * à la fois, choisie par pastille. Sous « Réseau EU » et « Réseau unitaire », chaque case porte le
 * nombre et la surface (« PM » et « ? » acceptés) ; ailleurs, le nombre. Les six destinations de
 * GHP DIGIT, « destination indéterminée » et le **débit du drain** suivent.
 *
 * ⚠️ **La colonne « Réseau unitaire » fige la règle du client** (`anomalyEffects`) à chaque
 * saisie : c'est la règle du jour du contrôle, qu'une règle changée ensuite ne réécrit pas.
 */
export interface StormwaterCardProps {
  survey: SurveyV2
  vocabulary: readonly VocabularyEntry[]
  /** Les règles du client du contrôle (#413) — « collecte non séparative » décide de l'unitaire. */
  anomalyEffects?: readonly ClientAnomalyEffect[]
  /** Fiche transmise : le tableau se lit, il ne bouge plus (D4). */
  disabled?: boolean
  onChange?: (survey: SurveyV2) => void
}

/** Le total, dans le format du bureau et du rapport (« 52,3 ») — jamais le flottant brut. */
const AREA = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 })

/**
 * Ce que la règle « collecte non séparative » du client vaut aujourd'hui, à figer sur une case
 * « Réseau unitaire » — `null` sans règle, ce qui compte comme une anomalie (#413).
 */
export function combinedSewerEffect(
  anomalyEffects: readonly ClientAnomalyEffect[] | undefined,
): string | null {
  const rule = anomalyEffects?.find((effect) => effect.code === 'COLLECTE_NON_SEPARATIVE')

  return rule === undefined ? null : effectFor(rule, null)
}

/** Ce qu'une saisie écrit dans une case, avec l'effet du client sous « Réseau unitaire ». */
export function writeOutdoorElement(
  survey: SurveyV2,
  cell: OutdoorElementCell,
  patch: Partial<Pick<OutdoorElement, 'count' | 'areaM2' | 'areaNote'>>,
  anomalyEffects: readonly ClientAnomalyEffect[] | undefined,
): SurveyV2 {
  return {
    ...survey,
    outdoorElements: setOutdoorElement(survey.outdoorElements, cell, {
      ...patch,
      ...(cell.destination === 'RESEAU_UNITAIRE'
        ? { effect: combinedSewerEffect(anomalyEffects) }
        : {}),
    }),
  }
}

export function StormwaterCard({
  survey,
  vocabulary,
  anomalyEffects,
  disabled,
  onChange,
}: StormwaterCardProps) {
  const drainId = useId()
  const outletsId = useId()
  const matrix = outdoorElementMatrix(survey, vocabulary, { all: true })
  // La première colonne tant que l'agent n'a rien choisi — relue à chaque rendu : le référentiel
  // peut descendre après l'ouverture de l'onglet.
  const [chosen, setColumn] = useState<string | null>(null)
  const at = Math.max(
    0,
    matrix.destinations.findIndex((destination) => destination.code === chosen),
  )
  const destination = matrix.destinations[at]
  const cells = survey.outdoorElements ?? []
  const total = (code: string) =>
    cells
      .filter((cell) => cell.destination === code)
      .reduce((sum, cell) => sum + (cell.count ?? 0), 0)
  const areaTotal = outdoorAreaTotals(survey).find((kept) => kept.destination === destination?.code)

  const write = (
    element: string,
    patch: Partial<Pick<OutdoorElement, 'count' | 'areaM2' | 'areaNote'>>,
  ) => {
    if (destination === undefined) return
    onChange?.(
      writeOutdoorElement(
        survey,
        { element, destination: destination.code },
        patch,
        anomalyEffects,
      ),
    )
  }

  const outlets: readonly string[] = survey.stormwaterOutlets ?? []

  function toggleOutlet(outlet: string): void {
    onChange?.({
      ...survey,
      stormwaterOutlets: outlets.includes(outlet)
        ? outlets.filter((kept) => kept !== outlet)
        : [...outlets, outlet],
    })
  }

  return (
    <Card>
      <CardBody className={s.card}>
        <SectionLabel>
          <Waves size={16} aria-hidden />
          Eaux pluviales
        </SectionLabel>
        <InfoNote tone="warn" icon={<TriangleAlert size={14} />}>
          Comptez chaque élément sous l’exutoire où il part, comme sur la fiche papier. Sous «
          Réseau EU », une case remplie est une anomalie ; sous « Réseau unitaire », la règle «
          collecte non séparative » du client décide.
        </InfoNote>

        {matrix.rows.length === 0 || destination === undefined ? (
          <InfoNote icon={<Info size={14} />}>
            La liste des éléments extérieurs n’est pas encore descendue sur cette tablette :
            synchronisez-la pour les compter.
          </InfoNote>
        ) : (
          <>
            <ToggleChipGroup aria-label="Exutoire de la fiche" className={grid.columns}>
              {matrix.destinations.map((heading) => (
                <ToggleChip
                  key={heading.code}
                  pressed={heading.code === destination.code}
                  count={total(heading.code) > 0 ? total(heading.code) : undefined}
                  onPressedChange={() => setColumn(heading.code)}
                >
                  {heading.label}
                </ToggleChip>
              ))}
            </ToggleChipGroup>

            <table className={grid.matrix}>
              <thead>
                <tr>
                  <th>Élément</th>
                  <th className={grid.count}>Nombre</th>
                  {destination.carriesArea && <th className={grid.count}>Surface</th>}
                </tr>
              </thead>
              {/* Une clé par colonne : une frappe refusée dans un exutoire ne passe pas au suivant. */}
              <tbody key={destination.code}>
                {matrix.rows.map((row) => {
                  const cell = row.cells[at] ?? null
                  const name = `${row.element.label}, ${destination.label}`

                  return (
                    <tr key={row.element.code} data-on={cell === null ? undefined : ''}>
                      <td>{row.element.label}</td>
                      <td className={grid.count}>
                        <CountInput
                          label={name}
                          value={cell?.count ?? null}
                          disabled={disabled}
                          onValueChange={(count) => write(row.element.code, { count })}
                        />
                      </td>
                      {destination.carriesArea && (
                        <td className={grid.count}>
                          <div className={s.measureRow}>
                            <NumberInput
                              // Remonté quand la note change : le champ non contrôlé reprend son vide.
                              key={cell?.areaNote ?? 'measure'}
                              className={s.areaField}
                              unit="m²"
                              aria-label={`Surface « ${name} » en m²`}
                              placeholder={
                                cell?.areaNote === 'memo'
                                  ? 'PM'
                                  : cell?.areaNote === 'unknown'
                                    ? '?'
                                    : '—'
                              }
                              defaultValue={cell?.areaNote == null ? (cell?.areaM2 ?? null) : null}
                              disabled={disabled === true || cell?.areaNote != null}
                              onValueChange={(areaM2) => write(row.element.code, { areaM2 })}
                            />
                            {(['memo', 'unknown'] as const).map((note) => (
                              <ToggleChip
                                key={note}
                                pressed={cell?.areaNote === note}
                                disabled={disabled}
                                aria-label={`Surface « ${name} » ${note === 'memo' ? 'pour mémoire' : 'inconnue'}`}
                                onPressedChange={() =>
                                  write(row.element.code, {
                                    areaNote: cell?.areaNote === note ? null : note,
                                  })
                                }
                              >
                                {note === 'memo' ? 'PM' : '?'}
                              </ToggleChip>
                            ))}
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
                {destination.carriesArea && (
                  <tr>
                    <td colSpan={2}>
                      <span className={s.totalLabel}>
                        {destination.code === 'RESEAU_UNITAIRE' ? 'Total unitaire' : 'Total EU'}
                      </span>
                    </td>
                    <td className={grid.count}>
                      <span className={s.totalValue}>{AREA.format(areaTotal?.areaM2 ?? 0)} m²</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {destination.carriesArea && (
              <p className={s.seen}>
                « PM » : pour mémoire · « ? » : surface inconnue — ni l’un ni l’autre ne compte au
                total.
              </p>
            )}
          </>
        )}

        {/* Les six cases du cadre « Raccordement Eaux Pluviales » de GHP DIGIT. */}
        <div className={s.outlets} role="group" aria-labelledby={outletsId}>
          <SectionLabel id={outletsId}>
            <Waves size={16} aria-hidden />
            Où partent les eaux pluviales
          </SectionLabel>
          <div className={s.toggles}>
            {STORMWATER_OUTLETS.map((outlet) => (
              <CheckRow
                key={outlet}
                checked={outlets.includes(outlet)}
                disabled={disabled}
                onCheckedChange={() => toggleOutlet(outlet)}
              >
                {STORMWATER_OUTLET_LABELS[outlet]}
              </CheckRow>
            ))}
          </div>
          <CheckRow
            checked={survey.stormwaterUndetermined === true}
            disabled={disabled}
            onCheckedChange={(stormwaterUndetermined) =>
              onChange?.({ ...survey, stormwaterUndetermined })
            }
          >
            Toutes ou partie des EP : destination indéterminée
          </CheckRow>
        </div>

        <div className={s.drain}>
          <SectionLabel className={s.drainLabel}>
            <Waves size={16} aria-hidden />
            <label htmlFor={drainId}>Débit du drain</label>
          </SectionLabel>
          <NumberInput
            id={drainId}
            className={s.drainField}
            unit="L/s"
            defaultValue={survey.drainFlowRateLps ?? null}
            disabled={disabled}
            onValueChange={(drainFlowRateLps) => onChange?.({ ...survey, drainFlowRateLps })}
          />
        </div>
        <InfoNote icon={<Info size={14} />}>
          Débit mesuré du drain, repris dans les données du contrôle et le rapport final.
        </InfoNote>
      </CardBody>
    </Card>
  )
}

/**
 * Ce que la frappe devient au relevé. `invalid` quand une saisie non vide n'est **pas** retenue :
 * la garder en silence enregistrerait autre chose que ce que l'écran affiche (M21).
 *
 * ⚠️ **La virgule est acceptée**, et ce n'est pas un ornement : le pavé numérique d'une tablette
 * française pose une virgule, et `Number('1,5')` vaut `NaN` — l'agent aurait vu sa mesure refusée
 * sans comprendre pourquoi.
 */
export function parseMeasure(raw: string): { value: number | null; invalid: boolean } {
  const cleaned = raw.replace(/\s/gu, '').replace(',', '.')
  if (cleaned === '') return { value: null, invalid: false }

  const parsed = Number(cleaned)
  if (Number.isFinite(parsed) && parsed >= 0) return { value: parsed, invalid: false }

  return { value: null, invalid: true }
}

/**
 * Un nombre mesuré et son unité — le `.val.editable` du prototype.
 *
 * ⚠️ **L'unité est posée à côté du champ, là où le prototype la range dedans** : `Input`
 * (`@ghp/ui`) n'a pas de slot de suffixe, et lui en ajouter un relève du design system — l'écran
 * jumeau du back-office porte le même champ.
 *
 * Non contrôlé, pour la raison de `NumberField` (`SurveyForm`) : la frappe vit dans le DOM, le
 * relevé suit à chaque touche. Un champ contrôlé reformaterait « 1,5 » sous les doigts.
 */
export function NumberInput({
  id,
  className,
  unit,
  defaultValue,
  disabled,
  onValueChange,
  placeholder = '—',
  'aria-label': label,
}: {
  id?: string
  className?: string
  unit: string
  placeholder?: string
  defaultValue: number | null
  disabled?: boolean
  onValueChange: (value: number | null) => void
  'aria-label'?: string
}) {
  const [invalid, setInvalid] = useState(false)
  const warningId = useId()

  return (
    <div className={className === undefined ? s.measure : `${s.measure} ${className}`}>
      <Input
        id={id}
        aria-label={label}
        aria-describedby={invalid ? warningId : undefined}
        // Le pavé numérique de la tablette, sans les flèches d'un `type="number"` — deux cibles
        // de 12 px collées au champ, injouables au doigt.
        inputMode="decimal"
        className={s.measureBox}
        placeholder={placeholder}
        defaultValue={defaultValue ?? ''}
        disabled={disabled}
        invalid={invalid}
        onValueChange={(raw) => {
          const parsed = parseMeasure(raw)
          setInvalid(parsed.invalid)
          onValueChange(parsed.value)
        }}
      />
      <span className={s.unit} aria-hidden>
        {unit}
      </span>
      {/* Une saisie qui n'est pas retenue **se dit** : le champ affiche la frappe, le relevé n'en
          garde rien — sans cette phrase, l'écart serait invisible (M21). */}
      {invalid && (
        <p id={warningId} className={s.notRetained}>
          Mesure non numérique — non enregistrée.
        </p>
      )}
    </div>
  )
}
